import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { driver, type Driver, type DriveStep } from 'driver.js';
import {
  ProductTourDefinition,
  ProductTourScope,
  ProductTourStep,
} from './product-tour.types';

const STORAGE_PREFIX = 'product-tour';

@Injectable({ providedIn: 'root' })
export class ProductTourService {
  private readonly translate = inject(TranslateService);

  private readonly tours = new Map<string, ProductTourDefinition>();
  private driverInstance: Driver | null = null;
  private markCompletedOnDestroy = true;
  private pendingTourId: string | null = null;
  private activeTourId: string | null = null;
  private starting = false;

  private readonly _scope = signal<ProductTourScope | null>(null);
  private readonly _running = signal(false);
  private readonly _lastCompletedId = signal<string | null>(null);

  $running = this._running.asReadonly();
  $lastCompletedId = this._lastCompletedId.asReadonly();

  setScope(scope: ProductTourScope | null): void {
    this._scope.set(scope);
  }

  registerTour(definition: ProductTourDefinition): void {
    this.tours.set(definition.id, definition);
  }

  isCompleted(tourId: string): boolean {
    const key = this.storageKey(tourId);
    if (!key || typeof localStorage === 'undefined') {
      return false;
    }
    return localStorage.getItem(key) === 'done';
  }

  resetTour(tourId: string): void {
    const key = this.storageKey(tourId);
    if (key && typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  }

  markCompleted(tourId: string): void {
    const key = this.storageKey(tourId);
    if (key && typeof localStorage !== 'undefined') {
      localStorage.setItem(key, 'done');
    }
  }

  /**
   * Modal bloqueante (termo, setup operacional):
   * pausa o tour ativo. O coordinator re-dispara ao desbloquear.
   */
  onBlockingModalChange(visible: boolean): void {
    if (!visible) {
      return;
    }
    if (this.driverInstance?.isActive() && this.activeTourId) {
      this.pendingTourId = this.activeTourId;
      this.stop(false);
    }
  }

  deferUntilUnblocked(tourId: string): void {
    this.pendingTourId = tourId;
  }

  consumePendingTourId(): string | null {
    const tourId = this.pendingTourId;
    this.pendingTourId = null;
    return tourId;
  }

  hasPending(): boolean {
    return !!this.pendingTourId;
  }

  stop(markCompleted = true): void {
    this.markCompletedOnDestroy = markCompleted;
    if (this.driverInstance?.isActive()) {
      this.driverInstance.destroy();
    }
    this.clearActiveState();
  }

  async startTour(
    tourId: string,
    options?: { force?: boolean }
  ): Promise<boolean> {
    if (typeof document === 'undefined') {
      return false;
    }

    if (this.starting || this.driverInstance?.isActive()) {
      return false;
    }

    if (!options?.force && this.isCompleted(tourId)) {
      return false;
    }

    const definition = this.tours.get(tourId);
    if (!definition?.steps.length) {
      return false;
    }

    this.starting = true;
    try {
      await this.waitForPaint();

      if (this.driverInstance?.isActive()) {
        return false;
      }

      const driveSteps = this.toDriveSteps(definition.steps);
      if (!driveSteps.length) {
        return false;
      }

      this.markCompletedOnDestroy = true;
      this.activeTourId = tourId;
      this._running.set(true);

      this.driverInstance = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        overlayOpacity: 0.55,
        stagePadding: 6,
        stageRadius: 10,
        popoverClass: 'n-product-tour-popover',
        nextBtnText: this.t('PRODUCT_TOUR.NEXT', 'Próximo'),
        prevBtnText: this.t('PRODUCT_TOUR.PREV', 'Anterior'),
        doneBtnText: this.t('PRODUCT_TOUR.DONE', 'Concluir'),
        progressText: '{{current}} / {{total}}',
        skipMissingElement: true,
        waitForElement: 400,
        steps: driveSteps,
        // onDestroyStarted com destroy() padrão NÃO limpa o tour (Driver.js).
        // Usar onDestroyed após o cleanup real.
        onDestroyed: () => {
          const completedId = this.activeTourId;
          if (this.markCompletedOnDestroy && completedId) {
            this.markCompleted(completedId);
            this._lastCompletedId.set(completedId);
          }
          this.clearActiveState();
        },
      });

      this.driverInstance.drive();
      return true;
    } finally {
      this.starting = false;
    }
  }

  private toDriveSteps(steps: ProductTourStep[]): DriveStep[] {
    const driveSteps: DriveStep[] = [];

    for (const step of steps) {
      if (!step.element) {
        driveSteps.push({
          popover: {
            title: this.t(step.titleKey, step.titleKey),
            description: this.t(step.descriptionKey, step.descriptionKey),
            side: 'top',
            align: 'center',
          },
        });
        continue;
      }

      if (!this.resolveStepElement(step)) {
        continue;
      }

      driveSteps.push({
        element: step.element,
        onHighlightStarted: () => {
          this.ensureBottomNavVisible();
          if (
            !document.querySelector(step.element!) &&
            this.hasNavOverflow()
          ) {
            this.openNavOverflowIfNeeded();
          }
        },
        popover: {
          title: this.t(step.titleKey, step.titleKey),
          description: this.t(step.descriptionKey, step.descriptionKey),
          side: 'top',
          align: 'center',
        },
      });
    }

    return driveSteps;
  }

  private resolveStepElement(step: ProductTourStep): Element | null {
    if (!step.element) {
      return null;
    }

    this.ensureBottomNavVisible();
    let el = document.querySelector(step.element);

    if (!el && (step.openOverflow || this.hasNavOverflow())) {
      this.openNavOverflowIfNeeded();
      el = document.querySelector(step.element);
    }

    return el;
  }

  private hasNavOverflow(): boolean {
    return !!document.querySelector('[data-tour="nav-overflow"]');
  }

  private openNavOverflowIfNeeded(): void {
    const overflowBtn = document.querySelector(
      '[data-tour="nav-overflow"]'
    ) as HTMLElement | null;
    if (!overflowBtn) {
      return;
    }
    overflowBtn.click();
  }

  private ensureBottomNavVisible(): void {
    const footer = document.querySelector('.n-menu-footer') as HTMLElement | null;
    if (footer?.classList.contains('n-nav-hidden')) {
      footer.classList.remove('n-nav-hidden');
    }
  }

  private storageKey(tourId: string): string | null {
    const scope = this._scope();
    if (!scope?.pessoaId || !scope?.estabelecimentoId) {
      return null;
    }
    return `${STORAGE_PREFIX}:${scope.pessoaId}:${scope.estabelecimentoId}:${tourId}`;
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return !value || value === key ? fallback : value;
  }

  private waitForPaint(): Promise<void> {
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(resolve, 120);
      });
    });
  }

  private clearActiveState(): void {
    this.activeTourId = null;
    this._running.set(false);
    this.driverInstance = null;
    this.markCompletedOnDestroy = true;
  }
}
